import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, Plus, Paperclip, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-white border-[var(--ir-border)] rounded-none focus-visible:ring-[var(--ir-blue)]";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const statusColor = { Open: "bg-[var(--ir-orange)]/10 text-[var(--ir-orange)] border-[var(--ir-orange)]/30", "In Progress": "bg-[var(--ir-blue)]/10 text-[var(--ir-blue)] border-[var(--ir-blue)]/25", Closed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" };

const StatusBadge = ({ s }) => <Badge className={`rounded-none border text-[10px] ${statusColor[s] || statusColor.Open}`}>{s}</Badge>;

export const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [cats, setCats] = useState([]);
  const [view, setView] = useState("list"); // list | new | thread
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ category: "", subject: "", message: "" });
  const [files, setFiles] = useState([]);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);

  const loadList = () => api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { loadList(); api.get("/tickets/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const openThread = (id) => api.get(`/tickets/${id}`).then((r) => { setActive(r.data); setView("thread"); setReply(""); setReplyFiles([]); }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  const submit = async () => {
    if (!form.category || !form.subject.trim() || !form.message.trim()) { toast.error("Category, subject and message are required"); return; }
    const fd = new FormData();
    fd.append("category", form.category); fd.append("subject", form.subject); fd.append("message", form.message);
    [...files].slice(0, 3).forEach((f) => fd.append("files", f));
    try { await api.post("/tickets", fd); toast.success("Ticket submitted"); setForm({ category: "", subject: "", message: "" }); setFiles([]); setView("list"); loadList(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    const fd = new FormData(); fd.append("message", reply);
    [...replyFiles].slice(0, 3).forEach((f) => fd.append("files", f));
    try { await api.post(`/tickets/${active.id}/reply`, fd); setReply(""); setReplyFiles([]); openThread(active.id); loadList(); toast.success("Reply sent"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="support-tickets">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-[var(--ir-orange)]" /><h2 className="font-display text-3xl text-[var(--ir-text)]">Support</h2></div>
        {view === "list" && <Button onClick={() => setView("new")} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none" data-testid="new-ticket-btn"><Plus className="w-4 h-4 mr-2" />New Ticket</Button>}
        {view !== "list" && <Button onClick={() => setView("list")} variant="outline" className="border-[var(--ir-border)] bg-white text-[var(--ir-text)] rounded-none" data-testid="back-to-tickets-btn"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>}
      </div>

      {view === "list" && (
        <div className="bg-white border border-[var(--ir-border)] rounded-lg soft-shadow divide-y divide-[var(--ir-border)]" data-testid="ticket-list">
          {tickets.length === 0 && <div className="p-8 text-center text-[var(--ir-muted)] text-sm">No tickets yet. Open one if you need help.</div>}
          {tickets.map((t) => (
            <button key={t.id} onClick={() => openThread(t.id)} className="w-full text-left p-4 hover:bg-[var(--ir-surface2)] flex items-center justify-between gap-3" data-testid={`ticket-item-${t.id}`}>
              <div><div className="text-[var(--ir-text)] text-sm font-medium">{t.subject}</div><div className="text-[var(--ir-muted)] text-xs">{t.category}</div></div>
              <div className="flex items-center gap-2"><StatusBadge s={t.status} />{!t.awaiting_admin && <Badge className="rounded-none border bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[10px]">Reply</Badge>}</div>
            </button>
          ))}
        </div>
      )}

      {view === "new" && (
        <div className="bg-white border border-[var(--ir-border)] rounded-lg soft-shadow p-6 max-w-2xl space-y-3" data-testid="new-ticket-form">
          <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className={`${inputCls} h-10`} data-testid="ticket-category-select"><SelectValue placeholder="Select a category" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c} data-testid={`ticket-cat-${c}`}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} data-testid="ticket-subject-input" /></div>
          <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className={inputCls} data-testid="ticket-message-input" /></div>
          <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Attachments (up to 3, max 10 MB each)</Label><Input type="file" multiple onChange={(e) => setFiles(e.target.files)} className={inputCls} data-testid="ticket-files-input" /></div>
          <Button onClick={submit} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none" data-testid="submit-ticket-btn"><Send className="w-4 h-4 mr-2" />Submit ticket</Button>
        </div>
      )}

      {view === "thread" && active && (
        <div className="bg-white border border-[var(--ir-border)] rounded-lg soft-shadow p-6 max-w-2xl" data-testid="ticket-thread">
          <div className="flex items-center justify-between mb-1"><h3 className="font-semibold text-[var(--ir-text)]">{active.subject}</h3><StatusBadge s={active.status} /></div>
          <p className="text-[var(--ir-muted)] text-xs mb-4">{active.category}</p>
          <div className="space-y-3">
            {active.messages.map((m) => (
              <div key={m.id} className={`p-3 rounded ${m.sender_role === "admin" ? "bg-[var(--ir-blue)]/5 border border-[var(--ir-blue)]/15" : "bg-[var(--ir-surface2)]"}`} data-testid={`msg-${m.id}`}>
                <div className="flex items-center justify-between"><span className="text-xs font-semibold text-[var(--ir-text)]">{m.sender_role === "admin" ? "Support" : m.sender_name}</span><span className="text-[var(--ir-muted)] text-[10px]">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span></div>
                <p className="text-[var(--ir-text)] text-sm whitespace-pre-wrap mt-1">{m.message}</p>
                {m.attachments.map((a, i) => (
                  <a key={i} href={`${BACKEND}/api/tickets/${active.id}/attachments/${m.id}/${i}`} className="inline-flex items-center gap-1 text-[var(--ir-blue)] text-xs mt-2 mr-3 underline" data-testid={`att-${m.id}-${i}`}><Paperclip className="w-3 h-3" />{a.filename}</a>
                ))}
              </div>
            ))}
          </div>
          {active.status !== "Closed" && (
            <div className="mt-4 border-t border-[var(--ir-border)] pt-4 space-y-2">
              <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Write a reply…" className={inputCls} data-testid="ticket-reply-input" />
              <Input type="file" multiple onChange={(e) => setReplyFiles(e.target.files)} className={inputCls} data-testid="ticket-reply-files" />
              <Button onClick={sendReply} disabled={!reply.trim()} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none disabled:opacity-50" data-testid="send-reply-btn"><Send className="w-4 h-4 mr-2" />Send reply</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
